import {
  ChakraFlex,
  ChakraVStack,
  ChakraText,
  FAIconName,
  FireflyButton,
  SMTAddCommentModal,
  SMTBaseSectionContainer,
  SMTFoodEntryModal,
  SMTDatePicker,
  SMTWeekLog,
  SMTDeleteEntryModal,
} from '@equip.health/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import { isEmpty } from 'lodash';

import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import SMTEmptySection from '~/components/maud/patients/profile/smt/SMTEmptySection';
import {
  formatSMTDate,
  formatSMTDay,
  populateMissingDailyLogs,
} from '~/components/maud/patients/profile/smt/utils';
import { useAddSMTComment } from '~/components/maud/patients/profile/smt/hooks/useAddSMTComment';
import { useAddSMTEntry } from '~/components/maud/patients/profile/smt/hooks/useAddSMTEntry';
import { useDeleteSMTEntry } from '~/components/maud/patients/profile/smt/hooks/useDeleteSMTEntry';
import { useEditSMTEntry } from '~/components/maud/patients/profile/smt/hooks/useEditSMTEntry';
import { useSMTDiscardModal } from '~/components/maud/patients/profile/smt/hooks/useSMTDiscardModal';
import useSMTSummary from '~/lib/hooks/useSMTSummary';
import { Path } from '~/routes/constants';
import AnalyticsEvents from '~/analytics';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGetSMTDailyLogsInfiniteQuery } from '~/store/services/SMT';
import {
  getSMTDailyLogsInitialPageParam,
  DEFAULT_DAYS_TO_LOAD,
} from '~/lib/util/smt.utils';
import SMTDailyLogWrapper from './SMTDailyLogWrapper';

const SCROLL_THRESHOLD = 100;

const SMT = ({
  isSMTEnabledForPatient,
  patientId,
}: {
  isSMTEnabledForPatient: boolean;
  patientId: string;
}) => {
  const { push } = useHistory();
  const { trackEvent } = useAnalytics();

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    DateTime.now().toJSDate(),
  );

  const [expandedLogs, setExpandedLogs] = useState<Record<string, boolean>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    activeDates,
    averageLogCount,
    datesWithLogs,
    handleMonthChange,
    totalLogCount,
  } = useSMTSummary(patientId, isSMTEnabledForPatient);

  const {
    handleAddEntry,
    handleCloseAddModal,
    handleConfirmAdd,
    isAddModalOpen,
    isLoading: isAddingLoading,
  } = useAddSMTEntry({ patientId });

  const {
    entryDataToEdit,
    handleCloseEditModal,
    handleConfirmEdit,
    handleEditEntry,
    isEditModalOpen,
    isLoading: isEditingLoading,
  } = useEditSMTEntry({ patientId });

  const {
    handleAddComment,
    handleCloseAddCommentModal,
    handleConfirmAddComment,
    isAddCommentModalOpen,
    isLoading: isAddingCommentLoading,
  } = useAddSMTComment({ patientId });

  const {
    handleCloseDeleteModal,
    handleConfirmDelete,
    handleDeleteEntry,
    isDeleteModalOpen,
    isLoading: isDeletingLoading,
  } = useDeleteSMTEntry({ patientId });

  const {
    handleDiscardModalBackClick,
    handleDiscardModalConfirm,
    handleDiscardModalDismiss,
    handleDiscardModalView,
  } = useSMTDiscardModal({ patientId });

  const [onFoodEntryModalClose, onFoodEntryModalSave] = useMemo(
    () => [
      isEditModalOpen ? handleCloseEditModal : handleCloseAddModal,
      isEditModalOpen ? handleConfirmEdit : handleConfirmAdd,
    ],
    [
      isEditModalOpen,
      handleCloseEditModal,
      handleCloseAddModal,
      handleConfirmEdit,
      handleConfirmAdd,
    ],
  );

  const selectedDateTime = selectedDate
    ? DateTime.fromJSDate(selectedDate)
    : DateTime.now();

  const selectedDateString =
    selectedDateTime.toISODate() || DateTime.now().toISODate()!;

  const {
    data: dailyLogsData,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useGetSMTDailyLogsInfiniteQuery(
    {
      patientId,
      selectedDate: selectedDateString,
    },
    {
      initialPageParam: getSMTDailyLogsInitialPageParam(selectedDateString),
      skip: !isSMTEnabledForPatient,
    },
  );

  const fetchedDaysCount = dailyLogsData?.pages.length
    ? dailyLogsData.pages.length * DEFAULT_DAYS_TO_LOAD
    : DEFAULT_DAYS_TO_LOAD;

  // Calculate date range for UI display purposes.
  // This mirrors the pagination logic in the service layer (getSMTDailyLogsInitialPageParam)
  // to ensure we fill in empty dates for the full range of fetched pages.
  // Note: If pagination logic changes in SMT.ts, this calculation must stay in sync.
  const startDate = selectedDateTime
    .minus({ days: fetchedDaysCount - 1 })
    .toISODate();
  const endDate = selectedDateTime.toISODate();

  // Fill in missing dates with empty logs
  const completeDailyLogs = useMemo(
    () =>
      populateMissingDailyLogs(dailyLogsData?.pages.flat(), startDate, endDate),
    [dailyLogsData?.pages, startDate, endDate],
  );

  // Memoize formatted dates to avoid recalculating in the map
  const formattedDailyLogs = useMemo(
    () =>
      completeDailyLogs.map((log) => ({
        ...log,
        formattedLogDate: formatSMTDate(log.logDate),
        logDay: formatSMTDay(log.logDate),
      })),
    [completeDailyLogs],
  );

  // Expand the first log with entry by default on initial load
  useEffect(() => {
    // Only set default expansion if expandedLogs is empty (initial state)
    if (
      Object.keys(expandedLogs).length === 0 &&
      !isEmpty(formattedDailyLogs)
    ) {
      const firstLogWithEntries = formattedDailyLogs.find(
        (log) => !isEmpty(log.smtEntries),
      );
      if (firstLogWithEntries) {
        setExpandedLogs({ [firstLogWithEntries.id]: true });
      }
    }
  }, [formattedDailyLogs, expandedLogs]);

  const handleGoToCarePlan = () => {
    trackEvent(AnalyticsEvents.goToCarePlanLinkClicked, {
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });
    push(`${Path.MAUD_PATIENTS}/${patientId}/care-planning`);
  };

  const handleDailyLogClick = (logId: string, logDate: string) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
    trackEvent(AnalyticsEvents.dailyLogAccordionClicked, {
      logDate,
      patientId,
    });
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setExpandedLogs({}); // Reset expansion state for new date range
    trackEvent(AnalyticsEvents.datePickerDateSelected, {
      date: date.toISOString(),
      patientId,
    });

    // Scroll to top when date changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isFetching || !hasNextPage) return;

    const target = e.currentTarget;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight <
      SCROLL_THRESHOLD;

    if (isNearBottom) {
      fetchNextPage();
    }
  };

  if (isError) {
    return (
      <ChakraFlex
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        padding="24px"
      >
        <ChakraText color="red.500" fontSize="md">
          Failed to load entries.
        </ChakraText>
      </ChakraFlex>
    );
  }

  const shouldShowEmptySection = !isSMTEnabledForPatient;
  return shouldShowEmptySection ? (
    <SMTEmptySection onGoToCarePlan={handleGoToCarePlan} />
  ) : (
    <>
      <ChakraFlex direction="row" gap="48px">
        <ChakraFlex flexDirection="column" gap="36px" minWidth="350px">
          <SMTWeekLog
            averageLogCount={averageLogCount}
            datesWithLogs={datesWithLogs}
            totalLogCount={totalLogCount}
          />
          <SMTDatePicker
            activeDates={activeDates}
            onDateSelect={handleDateSelect}
            onMonthChange={handleMonthChange}
            selectedDate={selectedDate}
          />
        </ChakraFlex>
        <SMTBaseSectionContainer
          HeaderComponent={
            <FireflyButton
              id="smt-section-entries-button"
              leftIcon={FAIconName.plusSolid}
              onClick={handleAddEntry}
              size="small"
            >
              Add entry
            </FireflyButton>
          }
          id="entries"
          title="Entries"
        >
          <ChakraVStack
            data-testid="smt-scroll-container"
            maxHeight="800px"
            onScroll={handleScroll}
            overflowY="auto"
            ref={scrollContainerRef}
            spacing="18px"
          >
            {formattedDailyLogs?.map((log) => (
              <SMTDailyLogWrapper
                dailyLog={log}
                id={log.id}
                isExpanded={!!expandedLogs[log.id]}
                key={log.id}
                logDate={log.formattedLogDate}
                logDay={log.logDay}
                onAddComment={handleAddComment}
                onDelete={handleDeleteEntry}
                onEdit={handleEditEntry}
                onToggle={() => {
                  if (!isEmpty(log.smtEntries)) {
                    handleDailyLogClick(log.id, log.logDate);
                  }
                }}
                patientId={patientId}
              />
            ))}
          </ChakraVStack>
        </SMTBaseSectionContainer>
      </ChakraFlex>
      <SMTFoodEntryModal
        confirmDiscardModalProps={{
          onBackClick: handleDiscardModalBackClick,
          onConfirm: handleDiscardModalConfirm,
          onDismiss: handleDiscardModalDismiss,
          onView: handleDiscardModalView,
        }}
        initialData={entryDataToEdit}
        isEditMode={isEditModalOpen}
        isLoading={isAddModalOpen ? isAddingLoading : isEditingLoading}
        isOpen={isAddModalOpen || isEditModalOpen}
        maxImageSizeMB={10}
        onClose={onFoodEntryModalClose}
        onSave={onFoodEntryModalSave}
      />
      <SMTAddCommentModal
        isLoading={isAddingCommentLoading}
        isOpen={isAddCommentModalOpen}
        onAddComment={handleConfirmAddComment}
        onClose={handleCloseAddCommentModal}
      />
      <SMTDeleteEntryModal
        isLoading={isDeletingLoading}
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirmDelete={handleConfirmDelete}
      />
    </>
  );
};

export default SMT;
